import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Lock, Users, ChefHat, Utensils } from 'lucide-react';
import { isDevBetaEnabled } from '../../utils/devMode';

interface MasterChefUnlockCardProps {
  userTier: 'demo' | 'starter' | 'core' | 'core_plus' | 'pro';
  onUpgrade: () => void;
  className?: string;
}

export function MasterChefUnlockCard({ 
  userTier, 
  onUpgrade, 
  className = '' 
}: MasterChefUnlockCardProps) {
  const isUnlocked = userTier === 'pro' || userTier === 'core_plus' || isDevBetaEnabled();
  
  if (isUnlocked) {
    // Show the actual feature if unlocked
    return (
      <Card className={`${className}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ChefHat className="w-5 h-5 text-coral" />
            Household & Equipment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-dusk">Family Size</label>
            <select className="w-full mt-1 px-3 py-2 border border-stone rounded-md bg-white">
              <option>1-2 people</option>
              <option>3-4 people</option>
              <option>5+ people</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-dusk">Kitchen Equipment</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {['Instant Pot', 'Air Fryer', 'Slow Cooker', 'Stand Mixer'].map((equipment) => (
                <Badge 
                  key={equipment}
                  variant="outline" 
                  className="cursor-pointer desktop-hover:bg-coral/10"
                >
                  {equipment}
                </Badge>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Show the unlock/upsell card if locked
  return (
    <Card className={`${className} relative overflow-hidden`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-supporting">
          <Lock className="w-5 h-5" />
          Household & Equipment
          <Badge variant="secondary" className="ml-auto bg-brand-100 text-brand-700 text-xs">
            Master Chef
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <h4 className="font-semibold text-dusk">Command your kitchen with Master Chef</h4>
          <p className="text-sm text-supporting">
            Batch prep, family scaling, and equipment-smart recipes.
          </p>
          
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-supporting">
              <Users className="w-4 h-4 text-brand-500" />
              <span>Scale recipes for your household size</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-supporting">
              <Utensils className="w-4 h-4 text-brand-500" />
              <span>Equipment-aware recipe recommendations</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-supporting">
              <ChefHat className="w-4 h-4 text-brand-500" />
              <span>Advanced meal prep optimization</span>
            </div>
          </div>
        </div>
        
        {/* Preview of locked controls */}
        <div className="space-y-3 opacity-50 pointer-events-none">
          <div>
            <label className="text-sm font-medium text-supporting">Family Size</label>
            <div className="w-full mt-1 px-3 py-2 border border-stone rounded-md bg-stone/20 text-supporting">
              Select household size...
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-supporting">Kitchen Equipment</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {['Instant Pot', 'Air Fryer', 'Slow Cooker'].map((equipment) => (
                <Badge 
                  key={equipment}
                  variant="outline" 
                  className="bg-stone/20 text-supporting border-stone"
                >
                  {equipment}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        
        <Button 
          onClick={onUpgrade}
          className="w-full bg-brand-500 desktop-hover:bg-brand-600 text-white font-medium"
        >
          Unlock with Master Chef
        </Button>
      </CardContent>
      
      {/* Subtle lock overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-stone/5 pointer-events-none" />
    </Card>
  );
}